import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { FaDownload, FaPlay, FaStop, FaSyncAlt, FaUserPlus } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import RecordingPlayer from '../components/RecordingPlayer';
import CustomerCreationForm from '../components/CustomerCreationForm';
import { useIsMobile } from '../hooks/useIsMobile';
import { fetchRecordingAudio, isRecordingAudioUnavailable, queryRecordings, syncElevateRecordings } from '../elevate/recordings.client';
import {
  externalParty,
  formatDuration,
  formatLondonDateTime,
  isUsableExternalNumber,
  type CallDirection,
  type DatePreset,
  type HuntGroupId,
  type NormalizedRecording,
  type RecordingUser,
} from '../elevate/recordings.model';
import { createJarvisContact, promoteJarvisContact } from '../elevate/contacts.client';
import { attachPhoneToSupplier, listSuppliersForPicker, type SupplierPickerRow } from '../elevate/suppliers.client';
import { loadIdentityIndex, resolveRecordingPhone } from '../elevate/identity.client';
import { findCustomersByPhone, identitySearchText, type IdentityIndex, type ResolvedIdentity } from '../elevate/identity';
import { suggestedNameFromCaller, type ContactDraft } from '../elevate/contacts.model';
import { emptyContactDraft } from './ContactsPage';
import { KNOWN_HUNT_GROUPS } from '../elevate/recordings.model';
import { normalizeUkDigits } from '../elevate/phone';

const PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'last_7', label: 'Last 7 days' },
  { id: 'last_30', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom' },
];

function isOfficeNumber(phone?: string | null): boolean {
  const digits = normalizeUkDigits(phone);
  if (!digits) return false;
  return KNOWN_HUNT_GROUPS.some((group) => group.digits.some((value) => normalizeUkDigits(value) === digits));
}

function partyPhone(party: { number: string | null; numberOriginal: string | null }): string | null {
  return party.number || party.numberOriginal;
}

function formatUnknownNumber(party: { number: string | null; numberOriginal: string | null }): string {
  return party.numberOriginal || party.number || '—';
}

function directionColor(direction: CallDirection): 'primary' | 'neutral' | 'warning' {
  if (direction === 'incoming') return 'primary';
  if (direction === 'outgoing') return 'neutral';
  return 'warning';
}

const CallRecordingsPage: React.FC = () => {
  const isMobile = useIsMobile();
  const blobUrlRef = useRef<string | null>(null);
  const [rows, setRows] = useState<NormalizedRecording[]>([]);
  const [users, setUsers] = useState<RecordingUser[]>([]);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [preset, setPreset] = useState<DatePreset>('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [query, setQuery] = useState('');
  const [direction, setDirection] = useState<CallDirection | 'all'>('all');
  const [ownerUserId, setOwnerUserId] = useState<string | 'all'>('all');
  const [huntGroup, setHuntGroup] = useState<HuntGroupId | 'all'>('all');
  const [page, setPage] = useState(1);
  const [activeAudio, setActiveAudio] = useState<{ key: string; url: string } | null>(null);
  const [audioBusyId, setAudioBusyId] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<Record<string, string>>({});
  const [identities, setIdentities] = useState<IdentityIndex>({ customers: [], contacts: [], suppliers: [] });
  const [addRow, setAddRow] = useState<NormalizedRecording | null>(null);
  const [addDraft, setAddDraft] = useState<ContactDraft>(emptyContactDraft());
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<SupplierPickerRow[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierPickerRow | null>(null);
  const [supplierLabel, setSupplierLabel] = useState('');
  const [promoteContact, setPromoteContact] = useState<{ id: string; displayName: string; firstName: string | null; lastName: string | null; phone: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const resolveParty = useCallback((party: { name: string | null; number: string | null; numberOriginal: string | null }): ResolvedIdentity => (
    resolveRecordingPhone(identities, partyPhone(party), party.name)
  ), [identities]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const [next, index] = await Promise.all([
        syncElevateRecordings(true),
        loadIdentityIndex(),
      ]);
      setRows(next.rows);
      setUsers(next.users);
      setLastSyncAt(next.lastSyncAt);
      setDisclaimer(next.disclaimer);
      setIdentities(index);
    } catch (err) {
      const typed = err as Error & { code?: string };
      setError(typed.message || 'Failed to load recordings');
      setErrorCode(typed.code || 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [load]);

  const verifiedHuntGroups = useMemo(
    () => [...new Set(rows.filter((row) => row.huntGroup !== 'unknown').map((row) => row.huntGroup))],
    [rows],
  );

  const result = useMemo(
    () => queryRecordings(rows, {
      preset,
      nowIso: new Date().toISOString(),
      customFrom,
      customTo,
      query,
      direction,
      ownerUserId,
      huntGroup,
      extraSearchText: (row) => [
        identitySearchText(resolveParty(row.from)),
        identitySearchText(resolveParty(row.to)),
      ].join(' '),
      page,
    }),
    [rows, preset, customFrom, customTo, query, direction, ownerUserId, huntGroup, page, resolveParty],
  );

  const resetFilters = () => {
    setPreset('last_30');
    setCustomFrom('');
    setCustomTo('');
    setQuery('');
    setDirection('all');
    setOwnerUserId('all');
    setHuntGroup('all');
    setPage(1);
  };

  const stopAudio = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setActiveAudio(null);
  };

  const playRow = async (row: NormalizedRecording) => {
    const key = `${row.ownerUserId}:${row.recordingId}`;
    if (activeAudio?.key === key) {
      stopAudio();
      return;
    }
    setAudioBusyId(key);
    try {
      const blob = await fetchRecordingAudio(row.ownerUserId, row.recordingId);
      stopAudio();
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setActiveAudio({ key, url });
    } catch (err) {
      const typed = err as Error & { code?: string };
      if (isRecordingAudioUnavailable(typed.code)) {
        setUnavailable((current) => ({ ...current, [key]: typed.message }));
      } else {
        setError(typed.message);
      }
    } finally {
      setAudioBusyId(null);
    }
  };

  const downloadRow = async (row: NormalizedRecording) => {
    const key = `${row.ownerUserId}:${row.recordingId}`;
    setAudioBusyId(key);
    try {
      const blob = await fetchRecordingAudio(row.ownerUserId, row.recordingId, true);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `elevate-recording-${row.recordingId}.mp3`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const typed = err as Error & { code?: string };
      if (isRecordingAudioUnavailable(typed.code)) {
        setUnavailable((current) => ({ ...current, [key]: typed.message }));
      } else {
        setError(typed.message);
      }
    } finally {
      setAudioBusyId(null);
    }
  };

  const openAddContact = (row: NormalizedRecording) => {
    const party = externalParty(row);
    if (!isUsableExternalNumber(party) || isOfficeNumber(partyPhone(party))) return;
    const identity = resolveParty(party);
    if (identity.kind !== 'unknown') return;
    const suggested = suggestedNameFromCaller(party.name && !/keystone/i.test(party.name) ? party.name : '');
    setAddRow(row);
    setAddDraft({
      ...emptyContactDraft(),
      firstName: suggested.firstName,
      lastName: suggested.lastName,
      displayName: suggested.displayName,
      phone: party.numberOriginal || party.number || '',
    });
    setAddSupplierOpen(false);
    setSelectedSupplier(null);
    setSupplierLabel('');
  };

  const closeAddModal = () => {
    setAddRow(null);
    setAddSupplierOpen(false);
    setSelectedSupplier(null);
    setSupplierLabel('');
  };

  const saveAddContact = async () => {
    if (!addRow) return;
    setSaving(true);
    setError(null);
    try {
      const party = externalParty(addRow);
      const result = await createJarvisContact({
        ...addDraft,
        phone: addDraft.phone || party.numberOriginal || party.number || '',
      });
      closeAddModal();
      setNotice(`${result.contact.displayName} saved. Matching recordings now show this name.`);
      setIdentities(await loadIdentityIndex());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add contact');
    } finally {
      setSaving(false);
    }
  };

  const openSupplierSection = async () => {
    setAddSupplierOpen(true);
    if (supplierOptions.length) return;
    try {
      setSupplierOptions(await listSuppliersForPicker());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load suppliers');
    }
  };

  const saveAddToSupplier = async () => {
    if (!addRow || !selectedSupplier) return;
    setSaving(true);
    setError(null);
    try {
      const party = externalParty(addRow);
      const phone = addDraft.phone || party.numberOriginal || party.number || '';
      const result = await attachPhoneToSupplier({
        supplierId: selectedSupplier.id,
        phone,
        label: supplierLabel || 'Recording',
      });
      closeAddModal();
      setNotice(result.alreadyHad
        ? `${result.supplierName} already has this number. Matching recordings now show this supplier.`
        : `${phone} added to ${result.supplierName}. Matching recordings now show this supplier.`);
      setIdentities(await loadIdentityIndex());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add this number to the supplier');
    } finally {
      setSaving(false);
    }
  };

  const openPromote = async (identity: Extract<ResolvedIdentity, { kind: 'contact' }>, phone: string | null) => {
    const index = await loadIdentityIndex();
    setIdentities(index);
    const existing = findCustomersByPhone(index.customers, phone);
    if (existing[0]) {
      try {
        await promoteJarvisContact(identity.contactId, existing[0].id);
        setNotice(`${existing[0].name} is already in the Directory, so the contact was removed from Contacts.`);
        setIdentities(await loadIdentityIndex());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not link this contact');
      }
      return;
    }
    const contact = index.contacts.find((row) => row.id === identity.contactId);
    setPromoteContact({
      id: identity.contactId,
      displayName: identity.displayName,
      firstName: contact?.firstName || identity.displayName.split(/\s+/)[0] || '',
      lastName: contact?.lastName || identity.displayName.split(/\s+/).slice(1).join(' ') || '',
      phone: contact?.phoneOriginal || phone || '',
    });
  };

  const finishPromote = async (customer: { id: string; name?: string }) => {
    if (!promoteContact) return;
    setSaving(true);
    setError(null);
    try {
      await promoteJarvisContact(promoteContact.id, customer.id);
      setPromoteContact(null);
      setNotice(`${customer.name || promoteContact.displayName} is now a Directory customer and was removed from Contacts.`);
      setIdentities(await loadIdentityIndex());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not promote contact');
    } finally {
      setSaving(false);
    }
  };

  const renderIdentity = (
    party: { name: string | null; number: string | null; numberOriginal: string | null },
    row: NormalizedRecording,
    showAction: boolean,
  ) => {
    const identity = resolveParty(party);
    const phone = partyPhone(party);
    const usable = showAction && isUsableExternalNumber(party) && !isOfficeNumber(phone);

    if (identity.kind === 'customer' && identity.href) {
      return (
        <Stack gap={0.25} alignItems={showAction ? 'flex-start' : 'flex-start'}>
          <Typography
            component={RouterLink}
            to={identity.href}
            level="body-sm"
            sx={{ color: 'primary.600', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            {identity.displayName}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Directory Customer</Typography>
        </Stack>
      );
    }

    if (identity.kind === 'supplier') {
      return (
        <Stack gap={0.25} alignItems="flex-start">
          <Typography
            component={RouterLink}
            to={identity.href}
            level="body-sm"
            sx={{ color: 'primary.600', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            {identity.displayName}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Supplier</Typography>
        </Stack>
      );
    }

    if (identity.kind === 'contact') {
      return (
        <Stack gap={0.5} alignItems="flex-start">
          <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{identity.displayName}</Typography>
            <Chip size="sm" variant="soft">Contact</Chip>
          </Stack>
          {usable && (
            <Button size="sm" variant="plain" onClick={() => void openPromote(identity, phone)}>
              Promote to Directory
            </Button>
          )}
        </Stack>
      );
    }

    return (
      <Stack gap={0.5} alignItems="flex-start">
        <Typography level="body-sm">{formatUnknownNumber(party)}</Typography>
        {usable && (
          <Button
            size="sm"
            variant="plain"
            startDecorator={<IconWrapper icon={FaUserPlus} />}
            onClick={() => openAddContact(row)}
          >
            Add Contact
          </Button>
        )}
      </Stack>
    );
  };

  const renderPlayer = (row: NormalizedRecording) => {
    if (activeAudio?.key !== `${row.ownerUserId}:${row.recordingId}`) return null;
    return (
      <Box sx={{ mt: isMobile ? 1 : 0 }}>
        <RecordingPlayer src={activeAudio.url} fallbackDurationSeconds={row.durationSeconds} />
      </Box>
    );
  };

  const renderActions = (row: NormalizedRecording) => {
    const key = `${row.ownerUserId}:${row.recordingId}`;
    if (unavailable[key]) {
      return <Typography level="body-xs" sx={{ color: 'danger.600' }}>{unavailable[key]}</Typography>;
    }
    const open = activeAudio?.key === key;
    return (
      <Stack direction="row" gap={0.5} justifyContent="flex-end" alignItems="center">
        <IconButton
          size="sm"
          variant="plain"
          aria-label={open ? 'Close player' : 'Play recording'}
          loading={audioBusyId === key}
          onClick={() => void playRow(row)}
        >
          <IconWrapper icon={open ? FaStop : FaPlay} />
        </IconButton>
        <IconButton
          size="sm"
          variant="plain"
          aria-label="Download recording"
          loading={audioBusyId === key}
          onClick={() => void downloadRow(row)}
        >
          <IconWrapper icon={FaDownload} />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography level="h2">Call Recordings</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Recorded calls only. Names come from Directory customers first, then Suppliers, then Contacts, then the number.
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
            {lastSyncAt ? `Last updated ${formatLondonDateTime(lastSyncAt)}` : 'Not synced yet'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startDecorator={<IconWrapper icon={FaSyncAlt} />}
          loading={loading}
          onClick={() => void load()}
        >
          Sync now
        </Button>
      </Stack>

      {errorCode === 'not_configured' && (
        <Alert color="warning" sx={{ mb: 2 }}>
          Elevate is not configured on the server. Add ELEVATE_CLIENT_ID and ELEVATE_CLIENT_SECRET, then restart JARVIS.
        </Alert>
      )}
      {error && errorCode !== 'not_configured' && (
        <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        {PRESETS.map((item) => (
          <Chip
            key={item.id}
            size="sm"
            variant={preset === item.id ? 'solid' : 'outlined'}
            onClick={() => {
              setPreset(item.id);
              setPage(1);
            }}
          >
            {item.label}
          </Chip>
        ))}
        <Button size="sm" variant="plain" onClick={resetFilters}>Reset filters</Button>
      </Stack>

      {preset === 'custom' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 1.5 }}>
          <Input type="date" value={customFrom} onChange={(event) => { setCustomFrom(event.target.value); setPage(1); }} aria-label="From date" />
          <Input type="date" value={customTo} onChange={(event) => { setCustomTo(event.target.value); setPage(1); }} aria-label="To date" />
        </Stack>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ mb: 2 }}>
        <Input
          placeholder="Search name or telephone number"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
          sx={{ flex: 1 }}
        />
        <Select
          value={direction}
          onChange={(_, value) => {
            setDirection((value as CallDirection | 'all') || 'all');
            setPage(1);
          }}
          sx={{ minWidth: 160 }}
        >
          <Option value="all">All directions</Option>
          <Option value="incoming">Incoming</Option>
          <Option value="outgoing">Outgoing</Option>
          <Option value="unknown">Unknown</Option>
        </Select>
        <Select
          value={ownerUserId}
          onChange={(_, value) => {
            setOwnerUserId((value as string | 'all') || 'all');
            setPage(1);
          }}
          sx={{ minWidth: 180 }}
        >
          <Option value="all">All recording users</Option>
          {users.map((user) => (
            <Option key={user.userId} value={user.userId}>{user.displayName}</Option>
          ))}
        </Select>
        {verifiedHuntGroups.length > 0 && (
          <Select
            value={huntGroup}
            onChange={(_, value) => {
              setHuntGroup((value as HuntGroupId | 'all') || 'all');
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            <Option value="all">All hunt groups</Option>
            {verifiedHuntGroups.map((group) => (
              <Option key={group} value={group}>{group === 'hq' ? 'HQ' : 'Glazing'}</Option>
            ))}
          </Select>
        )}
      </Stack>

      {loading && rows.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && result.total === 0 && !error && (
        <Alert color="neutral">
          {disclaimer || 'No recordings in this period. This list is recordings only.'}
        </Alert>
      )}

      {result.items.length > 0 && isMobile && (
        <Stack gap={1.5}>
          {result.items.map((row) => (
            <Card key={`${row.ownerUserId}:${row.recordingId}`} variant="outlined">
              <CardContent>
                <Typography level="title-sm">{formatLondonDateTime(row.startedAt)}</Typography>
                <Chip size="sm" color={directionColor(row.direction)} variant="soft" sx={{ my: 0.5 }}>
                  {row.direction}
                </Chip>
                <Box sx={{ my: 0.5 }}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>From</Typography>
                  {renderIdentity(row.from, row, row.direction !== 'outgoing')}
                </Box>
                <Box sx={{ my: 0.5 }}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>To</Typography>
                  {renderIdentity(row.to, row, row.direction === 'outgoing')}
                </Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  {row.huntGroupLabel} · {row.ownerDisplayName} · {formatDuration(row.durationSeconds)}
                </Typography>
                {renderActions(row)}
                {renderPlayer(row)}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {result.items.length > 0 && !isMobile && (
        <Table stripe="even" hoverRow sx={{ '& th:last-child, & td:last-child': { textAlign: 'right' } }}>
          <thead>
            <tr>
              <th>When</th>
              <th>Direction</th>
              <th>From</th>
              <th>To</th>
              <th>Hunt group</th>
              <th>Recorded by</th>
              <th>Duration</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {result.items.map((row) => (
              <React.Fragment key={`${row.ownerUserId}:${row.recordingId}`}>
                <tr>
                  <td>{formatLondonDateTime(row.startedAt)}</td>
                  <td>
                    <Chip size="sm" color={directionColor(row.direction)} variant="soft">{row.direction}</Chip>
                  </td>
                  <td>{renderIdentity(row.from, row, row.direction !== 'outgoing')}</td>
                  <td>{renderIdentity(row.to, row, row.direction === 'outgoing')}</td>
                  <td>{row.huntGroupLabel}</td>
                  <td>{row.ownerDisplayName}</td>
                  <td>{formatDuration(row.durationSeconds)}</td>
                  <td>{renderActions(row)}</td>
                </tr>
                {activeAudio?.key === `${row.ownerUserId}:${row.recordingId}` && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'left' }}>{renderPlayer(row)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      <Modal open={!!addRow} onClose={closeAddModal}>
        <ModalDialog sx={{ width: 420, maxWidth: 'calc(100vw - 24px)' }}>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.5 }}>
              Save as a contact (promote later to Directory), or add this number to an existing supplier.
            </Typography>
            <Stack gap={1.25}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={addDraft.displayName || `${addDraft.firstName} ${addDraft.lastName}`.trim()}
                  onChange={(event) => {
                    const value = event.target.value;
                    const parts = value.trim().split(/\s+/);
                    setAddDraft((current) => ({
                      ...current,
                      displayName: value,
                      firstName: parts[0] || '',
                      lastName: parts.slice(1).join(' '),
                    }));
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Telephone</FormLabel>
                <Input value={addDraft.phone} onChange={(event) => setAddDraft((current) => ({ ...current, phone: event.target.value }))} />
              </FormControl>
              <AccordionGroup sx={{ mt: 0.5 }}>
                <Accordion
                  expanded={addSupplierOpen}
                  onChange={(_, expanded) => {
                    if (expanded) void openSupplierSection();
                    else setAddSupplierOpen(false);
                  }}
                >
                  <AccordionSummary>Add to Supplier record</AccordionSummary>
                  <AccordionDetails>
                    <Stack gap={1.25}>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        Attach this number to an existing supplier instead of creating a contact.
                      </Typography>
                      <FormControl>
                        <FormLabel>Supplier</FormLabel>
                        <Autocomplete
                          options={supplierOptions}
                          value={selectedSupplier}
                          onChange={(_, value) => setSelectedSupplier(value)}
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          placeholder="Search existing suppliers"
                          slotProps={{ input: { autoComplete: 'off' } }}
                          renderOption={(props, option) => (
                            <AutocompleteOption {...props} key={option.id}>
                              <Box>
                                <Typography level="body-sm">{option.name}</Typography>
                                {option.phone && (
                                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{option.phone}</Typography>
                                )}
                              </Box>
                            </AutocompleteOption>
                          )}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Label (optional)</FormLabel>
                        <Input
                          placeholder="Recording"
                          value={supplierLabel}
                          onChange={(event) => setSupplierLabel(event.target.value)}
                        />
                      </FormControl>
                      <Button
                        loading={saving}
                        disabled={!selectedSupplier}
                        onClick={() => void saveAddToSupplier()}
                      >
                        Add to supplier
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </AccordionGroup>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={closeAddModal}>Cancel</Button>
            <Button loading={saving && !addSupplierOpen} onClick={() => void saveAddContact()}>Save contact</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!promoteContact} onClose={() => setPromoteContact(null)}>
        <ModalDialog sx={{ width: 640, maxWidth: 'calc(100vw - 24px)', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>Promote to Directory</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.5 }}>
              Complete the Directory customer details. The existing contact is kept and linked.
            </Typography>
            {promoteContact && (
              <CustomerCreationForm
                compact
                initialData={{
                  first_name: promoteContact.firstName || '',
                  last_name: promoteContact.lastName || '',
                  phone_number: promoteContact.phone,
                }}
                onCancel={() => setPromoteContact(null)}
                onCustomerCreated={(customer) => void finishPromote(customer)}
              />
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>

      {result.total > result.pageSize && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Showing {(result.page - 1) * result.pageSize + 1}–{Math.min(result.page * result.pageSize, result.total)} of {result.total}
          </Typography>
          <Stack direction="row" gap={1}>
            <Button size="sm" variant="plain" disabled={result.page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</Button>
            <Button size="sm" variant="plain" disabled={result.page * result.pageSize >= result.total} onClick={() => setPage((current) => current + 1)}>Next</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default CallRecordingsPage;
